import { fileUrl } from "./transport.js";

/**
 * Copies an image attachment to the OS clipboard, so it can be pasted into
 * another app or another chat.
 *
 * `ClipboardItem` only reliably accepts `image/png` across browsers — a
 * GIF or WebP blob handed to it directly is silently refused in some of
 * them — so anything not already PNG is re-encoded through a canvas first.
 * JPEG's transparency-free bytes survive that round trip losslessly enough
 * for a paste; only PNG's own alpha channel actually matters to preserve,
 * and PNG never needs the round trip at all.
 *
 * The blob is fetched with the session cookie, like the inline preview.
 *
 * Must be called synchronously from the click handler that granted the
 * clipboard-write permission: the async fetch is passed into `ClipboardItem`
 * as a promise rather than awaited first, which is what keeps this counting
 * as "triggered by a user gesture" in browsers that check.
 */
export async function copyImageToClipboard(fileId: string, contentType: string): Promise<void> {
  const blobPromise = fetch(fileUrl(`/api/v1/files/${fileId}/preview`), { credentials: "include" }).then(
    async (response) => {
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const raw = await response.blob();
      return contentType === "image/png" ? raw : reencodeAsPng(raw);
    },
  );

  await navigator.clipboard.write([new ClipboardItem({ "image/png": blobPromise })]);
}

function reencodeAsPng(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((png) => (png ? resolve(png) : reject(new Error("PNG encode failed"))), "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image decode failed"));
    };
    img.src = objectUrl;
  });
}
